export default `
uniform vec2 resolution;
uniform sampler2D bgTexture;
uniform vec2 bgTextureRuv;
uniform vec2 bgTextureDuv;
uniform sampler2D effectTexture;
uniform vec2 effectTextureRuv;
uniform vec2 effectTextureDuv;
uniform vec3 bgColor;
uniform vec3 bgColorHSL;
uniform float time;
uniform float sFactor;
uniform float gradientW;

#if defined(ZOOM_ON_SCROLL)
	uniform float zoom;
#elif defined(ZOOM_INFINITY)
	uniform float zoom1;
	uniform float zoom2;
	uniform float zoomMix;
	uniform float dx1;
	uniform float dx2;
	uniform float dy1;
	uniform float dy2;
#endif

#if defined(USE_BOKEH)
	uniform float bokehSize;
	uniform float bokehGlow;
	uniform vec3 bokehColorA;
	uniform vec3 bokehColorB;
	uniform vec3 bokehColorC;
	uniform vec3 bokehColorD;
	uniform vec3 bokehColorE;
#endif

#ifdef USE_MOUSE_COLORED_EFFECT
	uniform vec3 effectColor;
	uniform vec3 effectColorHSL;
#endif
#if defined(USE_MOUSE_BUFFER)
	uniform sampler2D mouseTexture;
#endif

vec2 position;

vec3 pal( in float t, in vec3 a, in vec3 b, in vec3 c, in vec3 d ){
    return a + b*cos( 6.28318*(c*t+d) );
}

vec3 hsl2rgb(vec3 hsl){
	if(hsl.y==0.){
		return vec3(hsl.z);
	}else{
		float b;
		if(hsl.z<.5){
			b=hsl.z*(1.+hsl.y);
		}else{
			b=hsl.z+hsl.y-hsl.y*hsl.z;
		}
		float a=2.*hsl.z-b;
		float hue = fract(hsl.x);
		vec3 hrgb = clamp(vec3(
			abs(hue*6.-3.)-1.,
			2.-abs(hue*6.-2.),
			2.-abs(hue*6.-4.)
		), 0., 1.);
		return a+hrgb*(b-a);
	}
}
vec3 mixColor(vec3 a, vec3 b){
	float aMin=min(min(a.r,a.g),a.b);
	float aMax=max(max(a.r,a.g),a.b);
	vec3 hsl=vec3(b.x, b.y, (aMax+aMin)/2.);
	return hsl2rgb(hsl);
}





#ifdef USE_BG_TEXTURE
	#if defined(MIX_DIFFERENCE)
		vec3 cmix(vec3 a, vec3 b){
			return abs(a - b);
		}
	#elif defined(MIX_LIGHTEN)
		vec3 cmix(vec3 a, vec3 b){
			return vec3(
				a.r > b.r ? a.r : b.r,
				a.g > b.g ? a.g : b.g,
				a.b > b.b ? a.b : b.b
			);
		}
	#elif defined(MIX_DARKEN)
		vec3 cmix(vec3 a, vec3 b){
			return vec3(
				a.r < b.r ? a.r : b.r,
				a.g < b.g ? a.g : b.g,
				a.b < b.b ? a.b : b.b
			);
		}
	#elif defined(MIX_MULTIPLY)
		vec3 cmix(vec3 a, vec3 b){
			return a * b;
		}
	#elif defined(MIX_COLOR)
		vec3 cmix(vec3 a, vec3 b){
			return mixColor(a, b);
		}
	#elif defined(MIX_NORMAL)
		vec3 cmix(vec3 a, vec3 b){
			return (a + b) / 2.;
		}
	#endif
#endif

#if defined(USE_MOUSE_COLORED_EFFECT) && defined(USE_BG_TEXTURE)
	#if defined(MOUSE_MIX_DIFFERENCE)
		vec3 mcmix(vec3 a, vec3 b){
			return abs(a - b);
		}
	#elif defined(MOUSE_MIX_LIGHTEN)
		vec3 mcmix(vec3 a, vec3 b){
			return vec3(
				a.r > b.r ? a.r : b.r,
				a.g > b.g ? a.g : b.g,
				a.b > b.b ? a.b : b.b
			);
		}
	#elif defined(MOUSE_MIX_DARKEN)
		vec3 mcmix(vec3 a, vec3 b){
			return vec3(
				a.r < b.r ? a.r : b.r,
				a.g < b.g ? a.g : b.g,
				a.b < b.b ? a.b : b.b
			);
		}
	#elif defined(MOUSE_MIX_MULTIPLY)
		vec3 mcmix(vec3 a, vec3 b){
			return a * b;
		}
	#elif defined(MOUSE_MIX_COLOR)
		vec3 mcmix(vec3 a, vec3 b){
			return mixColor(a, b);
		}
	#elif defined(MOUSE_MIX_NORMAL)
		vec3 mcmix(vec3 a, vec3 b){
			return (a + b) / 2.;
		}
	#endif
#endif

vec3 scene(vec3 bg, vec3 eff){
	#if defined(POINTER_TAIL) || defined(POINTER_PARTICLES)
		vec4 m = texture(mouseTexture, position);
		return mix(bg, eff, m.r);
	#else
		return bg * eff;
	#endif
}

#if defined(USE_BOKEH)
	mat2 rotMat(float a){
		float c=cos(a),s=sin(a);
		return mat2(c,-s,s,c);
	}

	float circle( vec2 p, float r ){
		return length(p) - r;
	}
	float random (vec2 p) {
		return fract(sin(mod(dot(p.xy ,vec2(12.9898,78.233)),3.14)) * 43758.5453);
	}
	float bokeh( vec2 p ){
		float w = 450.;
		if ( mod( p.y / w + 0.5, 2.0 ) < 1. ){
			p.x += w * .5;
		}
		float rnd = random( floor( p / w + .5 ) );
		float r = 30. + rnd * 40.;
		vec2 p2 = mod( p + 0.5 * w, w ) - .5 * w;
		float dist = circle( p2, r );
		float circle = 1. - smoothstep( 0., 1., dist * .04 );
		float glow = exp( -dist / 40. ) * .15 * bokehGlow * ( 1. - circle );
		return 3. * ( circle + glow ) * (.5 + fract(rnd*12345.)*.5);
	}
#endif

void main() {
	position = gl_FragCoord.xy / resolution;
	#if defined(USE_BG_TEXTURE)
		#if defined(ZOOM_ON_SCROLL)
			vec2 zPosition = position / zoom + .5 - .5/zoom;
		#elif defined(ZOOM_INFINITY)
			vec2 zPosition1 = position / zoom1 + .5 - .5/zoom1;
			vec2 zPosition2 = position / zoom2 + .5 - .5/zoom2;
		#endif
	#endif
	#if defined(POINTER_DISTORT)
		vec2 distort = texture(mouseTexture, position).xy * 0.135;
	#endif
	#if defined(USE_BG_TEXTURE)
		#if defined(ZOOM_ON_SCROLL)
			vec2 tPoint = zPosition * bgTextureRuv + bgTextureDuv;
			#if defined(POINTER_DISTORT)
				tPoint -= distort;
			#endif
			gl_FragColor = texture(bgTexture, tPoint);
		#elif defined(ZOOM_INFINITY)
			vec2 tPoint1 = zPosition1 * bgTextureRuv + bgTextureDuv + vec2(dx1, dy1);
			#if defined(POINTER_DISTORT)
				tPoint1 -= distort;
			#endif
			vec3 zCol1 = texture(bgTexture, tPoint1).rgb;
			vec2 tPoint2 = zPosition2 * bgTextureRuv + bgTextureDuv + vec2(dx2, dy2);
			#if defined(POINTER_DISTORT)
				tPoint2 -= distort;
			#endif
			vec3 zCol2 = texture(bgTexture, tPoint2).rgb;
			gl_FragColor.rgb = mix(zCol1, zCol2, zoomMix);
			gl_FragColor.a = 1.;
		#else
			vec2 tPoint = position * bgTextureRuv + bgTextureDuv;
			#if defined(POINTER_DISTORT)
				tPoint -= distort;
			#endif
			gl_FragColor = texture(bgTexture, tPoint);
		#endif
		#if !defined(MIX_NO)
			#if defined(MIX_COLOR)
				gl_FragColor.rgb = cmix(gl_FragColor.rgb, bgColorHSL);
			#else
				gl_FragColor.rgb = cmix(gl_FragColor.rgb, bgColor);
			#endif
		#endif
	#else
		gl_FragColor = vec4(bgColor, 1.);
	#endif
	#ifdef USE_MOUSE_COLORED_EFFECT
		#if defined(USE_BG_TEXTURE)
			#if defined(ZOOM_INFINITY)
				vec2 effPoint = position * bgTextureRuv + bgTextureDuv;
			#else
				float mZoom = 1. + .3 * abs(mod(time*.1, 1.) - .5);
				vec2 zMPosition = position / mZoom + .5 - .5/mZoom;
				vec2 effPoint = zMPosition * bgTextureRuv + bgTextureDuv;
			#endif
			vec3 effT = texture(bgTexture, effPoint).rgb;
			#if defined(MOUSE_MIX_NO)
				#if !defined(MIX_NO)
					#if defined(MIX_COLOR)
						effT = cmix(effT, bgColorHSL);
					#else
						effT = cmix(effT, bgColor);
					#endif
				#endif
				gl_FragColor.rgb = scene(gl_FragColor.rgb, effT);
			#else
				#if defined(MOUSE_MIX_COLOR)
					gl_FragColor.rgb = scene(gl_FragColor.rgb, mcmix(effT, effectColorHSL));
				#else
					gl_FragColor.rgb = scene(gl_FragColor.rgb, mcmix(effT, effectColor));
				#endif
			#endif
		#else
			gl_FragColor.rgb = scene(gl_FragColor.rgb, effectColor);
		#endif
	#endif

	#ifdef USE_BOKEH
		vec2 p = ( gl_FragCoord.xy - 0.5*resolution.xy ) / max(resolution.x, resolution.y) * 2000.0/(bokehSize != 0. ? bokehSize : 1.);
		p *= rotMat( 0.2 + time * 0.03 );
		gl_FragColor.rgb += bokehColorA * bokeh( p + vec2( -50.0 * time +  0.0, 0.0  ) );
		p *= rotMat( 0.3 - time * 0.05 );
		gl_FragColor.rgb += bokehColorB * bokeh( p + vec2( -70.0 * time + 33.0, -33.0 ) );
		p *= rotMat( 0.5 + time * 0.07 );
		gl_FragColor.rgb += bokehColorC * bokeh( p + vec2( -60.0 * time + 55.0, 55.0 ) );
		p *= rotMat( 0.9 - time * 0.03 );
		gl_FragColor.rgb += bokehColorD * bokeh( p + vec2( -25.0 * time + 77.0, 77.0 ) );
		p *= rotMat( 0.0 + time * 0.05 );
		gl_FragColor.rgb += bokehColorE * bokeh( p + vec2( -15.0 * time + 99.0, 99.0 ) );
	#endif
	#ifdef USE_GRADIENT
		//float mm = 1. - smoothstep(1. - gradientW + sFactor*gradientW, 1.000001,position.y);
		float rx = abs(position.x - .5);
		float mm = min(1., (1. - (position.y - sFactor))/gradientW + rx*rx );
		gl_FragColor.rgb = mix( gl_FragColor.rgb, bgColor, mm*.96 );
	#endif
}
`
